import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Camera,
  X,
  RotateCcw,
  Check,
  AlertCircle,
  RefreshCw,
  ImageIcon,
} from 'lucide-react';

const CameraCapture = ({ isOpen, onClose, onCapture, title = 'Take Photo' }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const [hasPermission, setHasPermission] = useState(null); // null = not asked, true = granted, false = denied
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const [facingMode, setFacingMode] = useState('environment'); // 'environment' = back camera, 'user' = front
  const [availableCameras, setAvailableCameras] = useState([]);
  const [videoReady, setVideoReady] = useState(false);

  // Check if camera API is available
  const isCameraSupported = () => {
    return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
  };

  // Request camera permission and start stream
  const startCamera = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    // Check if camera API is available
    if (!isCameraSupported()) {
      setIsLoading(false);
      setHasPermission(false);
      setError('Camera not available. Please use the gallery option to select a photo, or ensure you are using HTTPS.');
      return;
    }

    try {
      // Stop any existing stream
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }

      // Request camera access
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: facingMode,
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      });

      streamRef.current = stream;
      setHasPermission(true);

      // Wait a bit for the video element to be ready
      setTimeout(() => {
        if (videoRef.current && streamRef.current) {
          videoRef.current.srcObject = streamRef.current;
          videoRef.current.play().catch(e => {
            console.error('Video play error:', e);
          });
        }
      }, 100);

      // Get list of available cameras
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const cameras = devices.filter(device => device.kind === 'videoinput');
        setAvailableCameras(cameras);
      } catch (e) {
        // Enumerate devices may fail but camera still works
        console.log('Could not enumerate devices:', e);
      }

    } catch (err) {
      console.error('Camera error:', err);
      setHasPermission(false);

      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setError('Camera permission denied. Please allow camera access in your browser settings.');
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        setError('No camera found on this device.');
      } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
        setError('Camera is already in use by another application.');
      } else if (err.name === 'NotSupportedError') {
        setError('Camera not supported. Make sure you are using HTTPS.');
      } else {
        setError(`Unable to access camera: ${err.message || 'Unknown error'}`);
      }
    } finally {
      setIsLoading(false);
    }
  }, [facingMode]);

  // Start camera when modal opens
  useEffect(() => {
    if (isOpen) {
      setCapturedImage(null);
      setVideoReady(false);
      startCamera();
    }

    return () => {
      // Cleanup: stop camera when component unmounts or modal closes
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
    };
  }, [isOpen, startCamera]);

  // Switch camera (front/back)
  const switchCamera = () => {
    setFacingMode(prev => prev === 'environment' ? 'user' : 'environment');
  };

  // Capture photo
  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    // Set canvas size to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw video frame to canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Get base64 image
    const imageData = canvas.toDataURL('image/jpeg', 0.85);
    setCapturedImage(imageData);
  };

  // Retake photo
  const retakePhoto = () => {
    setCapturedImage(null);
  };

  // Confirm and use photo
  const confirmPhoto = () => {
    if (capturedImage) {
      onCapture(capturedImage);
      handleClose();
    }
  };

  // Handle close
  const handleClose = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setCapturedImage(null);
    setError(null);
    onClose();
  };

  // Handle file upload as fallback
  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        onCapture(reader.result);
        handleClose();
      };
      reader.readAsDataURL(file);
    }
  };

  // Request permission again
  const requestPermissionAgain = async () => {
    // Clear error and try again
    setError(null);
    setHasPermission(null);
    await startCamera();
  };

  if (!isOpen) return null;

  return (
    <div className="qconfig-camera-overlay">
      <div className="qconfig-camera-modal">
        {/* Header */}
        <div className="qconfig-camera-header">
          <h3>{title}</h3>
          <button onClick={handleClose} className="qconfig-camera-close-btn">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="qconfig-camera-content">
          {isLoading && (
            <div className="qconfig-camera-loading">
              <RefreshCw className="w-8 h-8 animate-spin" />
              <p>Requesting camera access...</p>
            </div>
          )}

          {error && (
            <div className="qconfig-camera-error">
              <AlertCircle className="w-12 h-12" />
              <p>{error}</p>
              <div className="qconfig-camera-error-actions">
                <button onClick={requestPermissionAgain} className="qconfig-camera-retry-btn">
                  <RefreshCw className="w-4 h-4" />
                  Try Again
                </button>
                <label className="qconfig-camera-fallback-btn">
                  <Camera className="w-4 h-4" />
                  Take Photo (Native)
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={handleFileUpload}
                    hidden
                  />
                </label>
                <label className="qconfig-camera-fallback-btn">
                  <ImageIcon className="w-4 h-4" />
                  Choose from Gallery
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileUpload}
                    hidden
                  />
                </label>
              </div>
            </div>
          )}

          {!isLoading && !error && !capturedImage && (
            <div className="qconfig-camera-preview">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="qconfig-camera-video"
                style={{ display: videoReady ? 'block' : 'none' }}
                onLoadedMetadata={(e) => {
                  e.target.play().catch(err => console.error('Play error:', err));
                }}
                onPlaying={() => setVideoReady(true)}
              />
              {!videoReady && (
                <div className="qconfig-camera-waiting">
                  <RefreshCw className="w-6 h-6 animate-spin" />
                  <p>Starting camera...</p>
                  <label className="qconfig-camera-fallback-btn" style={{ marginTop: '1rem' }}>
                    <ImageIcon className="w-4 h-4" />
                    Or choose from Gallery
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileUpload}
                      hidden
                    />
                  </label>
                </div>
              )}
              <canvas ref={canvasRef} style={{ display: 'none' }} />

              {/* Camera controls */}
              <div className="qconfig-camera-controls">
                {availableCameras.length > 1 && (
                  <button onClick={switchCamera} className="qconfig-camera-switch-btn">
                    <RotateCcw className="w-5 h-5" />
                  </button>
                )}
                <button onClick={capturePhoto} className="qconfig-camera-capture-btn">
                  <Camera className="w-8 h-8" />
                </button>
                <label className="qconfig-camera-gallery-btn">
                  <ImageIcon className="w-5 h-5" />
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileUpload}
                    hidden
                  />
                </label>
              </div>
            </div>
          )}

          {capturedImage && (
            <div className="qconfig-camera-captured">
              <img src={capturedImage} alt="Captured" className="qconfig-camera-captured-image" />

              {/* Confirm/Retake controls */}
              <div className="qconfig-camera-confirm-controls">
                <button onClick={retakePhoto} className="qconfig-camera-retake-btn">
                  <RotateCcw className="w-5 h-5" />
                  Retake
                </button>
                <button onClick={confirmPhoto} className="qconfig-camera-confirm-btn">
                  <Check className="w-5 h-5" />
                  Use Photo
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CameraCapture;
